import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import ActivateDeactivateModal from '../../common/ActivateDeactivateModal';
import CommonModal from '../../common/CommonModal';
import { toast } from 'react-toastify';
import { convertUTCToLocal } from '../../utils/dateUtils';
import styles from './list.module.css';

const OrganizationList = ({ persona }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [t] = useTranslation();
  const [confirmActivateModal, setConfirmActivateModal] = useState({
    open: false,
    record: null,
  });
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({
    open: false,
    record: null,
  });
  const [paginationParams, setPaginationParams] = useState({
    limit: 10,
    offset: 0,
    sortField: 'createdAt',
    sortOrder: -1,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganizations();
  }, [paginationParams]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance
        .get('/api/v1/organizations/', { withCredentials: true })
        .catch((err) => {
          console.log(err);
        });

      setData(response?.data?.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const closeDeleteModal = () =>
    setConfirmDeleteModal({ open: false, record: null });

  const confirmDeleteOrganization = async () => {
    const org = confirmDeleteModal.record;
    closeDeleteModal();
    if (!org) return;

    try {
      await axiosInstance.delete(`/api/v1/organizations/${org.id}`);
      toast.success(t('organization_deleted_successfully'));
      fetchOrganizations();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        t('failed_to_delete_organization');
      toast.error(errorMessage);
    }
  };

  const onDataTableEvent = (event) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: event.rows,
      offset: event.first,
      sortField: event.sortField || prev.sortField,
      sortOrder: event.sortOrder || prev.sortOrder,
    }));
  };

  const columns = [
    { field: 'id', header: 'ID', sortable: true },
    {
      field: 'code',
      header: 'Organization Code',
      sortable: true,
      filter: true,
      // body: (rowData) =>
      //   rowData.code || `ORG-${String(rowData.id).padStart(5, '0')}`,
    },
    {
      field: 'name',
      header: 'Organization Name',
      sortable: true,
      filter: true,
    },
    {
      field: 'type',
      header: 'Type',
      sortable: true,
      filter: true,
      body: (rowData) => (
        <span
          className={`badge ${rowData.type === 'internal' ? 'badge-success' : 'badge-info'}`}
        >
          {rowData.type}
        </span>
      ),
    },
    {
      field: 'category.name',
      header: 'Category',
      sortable: true,
      filter: true,
      body: (rowData) =>
        rowData.type === 'external' ? (
          rowData.category?.name || (
            <span className={styles.placeholderText}>Not assigned</span>
          )
        ) : (
          <span className={styles.placeholderText}>N/A</span>
        ),
    },
    {
      field: 'createdAt',
      header: 'Created At',
      sortable: true,
      filter: true,
      body: (rowData) => convertUTCToLocal(rowData.createdAt),
    },
  ];

  const actions = [
    {
      text: 'View',
      color: 'primary',
      onClick: (row) => navigate(`/${persona}/organization/details/${row.id}`),
    },
    {
      text: t('manage'),
      color: 'secondary',
      onClick: (row) =>
        navigate(`/${persona}/organization/structure/${row.id}`),
    },
    {
      text: t('delete'),
      color: 'primary',
      status: 'danger',
      onClick: (row) => setConfirmDeleteModal({ open: true, record: row }),
    },
  ];

  return (
    <div>
      <DataTableComponent
        data={data}
        columns={columns}
        loading={loading}
        paginationParams={paginationParams}
        totalRecords={data?.length}
        onDataTableEvent={onDataTableEvent}
        enableCreate={true}
        enablePagination={true}
        enableLazyLoading={false}
        enableExport={true}
        actions={actions}
        sectionName={`Organizations-${new Date().getDate()}-${new Date().getMonth() + 1}-${new Date().getFullYear()}-${new Date().getHours()}-${new Date().getMinutes()}`}
      />

      {confirmActivateModal.open && (
        <ActivateDeactivateModal
          sentData={confirmActivateModal.record}
          onEvent={setConfirmActivateModal}
          onConfirm={fetchOrganizations}
          apiPath="organizations"
          axios={axiosInstance}
          columnName="name"
          dbAction="active"
        />
      )}

      <CommonModal
        open={confirmDeleteModal.open}
        onClose={closeDeleteModal}
        title={t('delete_organization')}
        message={t('confirm_delete_organization')}
        size="small"
        variant="warning"
        buttonProps={[
          {
            label: t('cancel'),
            onClick: closeDeleteModal,
          },
          {
            label: t('delete'),
            onClick: confirmDeleteOrganization,
          },
        ]}
      />
    </div>
  );
};

export default OrganizationList;
